import { ChevronDown } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';

import type { IDeviceListItem } from '@/feature/device/types/device';
import Button from '@/shared/components/ui/Button';

import type { DeviceAction, IDeviceActionHandler } from './deviceActionTypes';

interface IDeviceActionsDropdownProps {
  device: IDeviceListItem;
  onAction: IDeviceActionHandler;
  openUpward?: boolean;
  align?: 'left' | 'right';
}

const menuBtnClass =
  'w-full text-left px-4 py-2.5 text-sm font-semibold text-gray-800 hover:bg-gray-50 cursor-pointer bg-transparent border-0';
const dangerBtnClass =
  'w-full text-left px-4 py-2.5 text-sm font-semibold text-red-700 hover:bg-red-50 cursor-pointer bg-transparent border-0';

const DeviceActionsDropdown = ({
  device,
  onAction,
  openUpward = false,
  align = 'left',
}: IDeviceActionsDropdownProps) => {
  const [open, setOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const status = device.device_status?.toLowerCase();

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    if (open) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  const run = (action: DeviceAction) => {
    setOpen(false);
    onAction(action, device);
  };

  if (status === 'unfit') {
    return (
      <p className='text-sm text-gray-500 m-0'>
        This device is retired and unavailable for rental.
      </p>
    );
  }

  return (
    <div className='relative inline-block' ref={menuRef}>
      <Button
        type='button'
        variant='primary'
        aria-expanded={open}
        onClick={() => setOpen((prev) => !prev)}
        rightIcon={
          <ChevronDown
            size={16}
            className={open ? 'rotate-180 transition-transform' : 'transition-transform'}
          />
        }
      >
        Actions
      </Button>
      {open && (
        <div
          className={`absolute z-50 min-w-[220px] bg-white border border-gray-200 rounded-lg shadow-lg py-1 ${
            align === 'right' ? 'right-0' : 'left-0'
          } ${openUpward ? 'bottom-full mb-2' : 'top-full mt-2'}`}
        >
          {status === 'available' && (
            <>
              <button type='button' className={menuBtnClass} onClick={() => run('assign')}>
                Assign / Rent Device
              </button>
              <button type='button' className={menuBtnClass} onClick={() => run('maintenance')}>
                Maintenance
              </button>
              <div className='h-px bg-gray-100 my-1' />
              <button type='button' className={dangerBtnClass} onClick={() => run('unfit')}>
                Mark as Unfit
              </button>
            </>
          )}
          {status === 'rented' && (
            <>
              <button type='button' className={menuBtnClass} onClick={() => run('extend')}>
                Extend Rental
              </button>
              <button type='button' className={menuBtnClass} onClick={() => run('release')}>
                Release Device
              </button>
              <button type='button' className={menuBtnClass} onClick={() => run('maintenance')}>
                Maintenance
              </button>
              <div className='h-px bg-gray-100 my-1' />
              <button type='button' className={dangerBtnClass} onClick={() => run('unfit')}>
                Mark as Unfit
              </button>
            </>
          )}
          {status === 'maintenance' && (
            <>
              <button
                type='button'
                className={menuBtnClass}
                onClick={() => run('complete-maintenance')}
              >
                Complete Maintenance
              </button>
              {device.rented_to ? (
                <button type='button' className={menuBtnClass} onClick={() => run('release')}>
                  Release Device
                </button>
              ) : null}
              <div className='h-px bg-gray-100 my-1' />
              <button type='button' className={dangerBtnClass} onClick={() => run('unfit')}>
                Mark as Unfit
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default DeviceActionsDropdown;
